import { useState } from 'react';

import type { CashAccountBalance } from '@/api/cash';
import { enterKeyScope, useAutoFocusFirstField } from '@/components/enterKeyNavigation';
import { Button, Card, Notice, PageHeading } from '@/components/PageHeading';
import { Field, controlClass } from '@/components/Field';
import { formatAmount } from '@core/format/money';
import { depositTheRest, type TransferDraft } from '@core/cash/transfer';
import { toIsoDate } from '@core/format/dates';
import {
  countedText,
  drawerNotes,
  emptyNoteCount,
  noteValue,
  type DrawerNote,
  type NoteCount,
} from '@core/cash/count';
import { countProblem, outcomeLabels, outcomeOf, reasonRequired } from './count';

/**
 * Counting the drawer at the end of the day (spec Section 18's reconciliation) — board 6 of the canvas
 * "Cash & expense flow": note by note, as the phone counts, with the book beside it.
 *
 * <p>
 * <strong>A page, not a drawer</strong>, per the rule settled 2026-09-08: a screen that writes a
 * record is a page. Reached by internal state from the cash book beside it, because the shell
 * navigates by key and has no router.
 * </p>
 *
 * <p>
 * <strong>The variance is shown before Save, and that is the whole design of the screen.</strong>
 * A count that silently posts an adjustment is a shop finding out next month that its drawer was
 * corrected by two hundred rupees; showing the difference as it is typed makes the correction
 * something a person agrees to. The figure printed here is what this browser worked out — the
 * record keeps the server's, frozen at the moment of saving, and the two can differ by whatever
 * lands in between.
 * </p>
 */
export function CountDrawerPage({
  accounts,
  allAccounts,
  countedToday,
  initialAccountId,
  busy = false,
  onCancel,
  onSave,
}: {
  /** The account chosen on the board or on Count & check; the first countable one otherwise. */
  initialAccountId?: number | undefined;
  /** Only accounts a shop can actually count — see `countableAccounts`. */
  accounts: CashAccountBalance[];
  /** Every account the branch holds — where "deposit the rest" finds the bank. */
  allAccounts: CashAccountBalance[];
  /** Account ids already counted for the day shown, so the screen refuses before the server does. */
  countedToday: (accountId: number, day: string) => boolean;
  busy?: boolean;
  onCancel: () => void;
  /** `deposit` is the move to open afterwards — "Record and deposit the rest" — or null. */
  onSave: (
    accountId: number,
    input: { counted: number; countedOn: string; notes: string | null },
    deposit: Partial<TransferDraft> | null,
  ) => void;
}) {
  const firstField = useAutoFocusFirstField<HTMLInputElement>();

  const [today] = useState(() => toIsoDate(new Date()) ?? '');

  const [accountId, setAccountId] = useState<number | ''>(
    accounts.find((a) => a.id === initialAccountId)?.id ?? accounts[0]?.id ?? '',
  );
  // Note by note, as the phone counts (core `countedFromNotes`), or the total typed whole.
  const [byNotes, setByNotes] = useState(true);
  const [notesCount, setNotesCount] = useState<NoteCount>(emptyNoteCount);
  const [typedTotal, setTypedTotal] = useState('');
  const [countedOn, setCountedOn] = useState(() => toIsoDate(new Date()) ?? '');
  const [notes, setNotes] = useState('');

  const counted = byNotes ? countedText(notesCount) : typedTotal;
  const account = accounts.find((a) => a.id === accountId);

  // What the ledger says right now, whole-history — which is what the account card on the cash book
  // is showing this person, so the two figures on screen agree. A count of a past day is worked out
  // against a different figure by the server, and the notice below says so rather than pretending
  // this one is authoritative.
  const expected = account?.closing ?? 0;

  const value = Number(counted);
  const variance = Number.isFinite(value) && counted.trim() !== '' ? value - expected : 0;
  const outcome = outcomeOf(variance);

  const problem =
    accountId === ''
      ? 'There is nothing to count at this branch yet.'
      : countProblem({
          counted,
          countedOn,
          today,
          notes,
          expected,
          alreadyCounted: countedToday(Number(accountId), countedOn),
        });

  // Keep the till's float and bank the rest — offered, never forced (owner, 2026-10-22).
  const rest =
    counted.trim() !== '' && Number.isFinite(value)
      ? depositTheRest(value, account, allAccounts)
      : null;

  const record = (deposit: Partial<TransferDraft> | null) =>
    onSave(Number(accountId), { counted: value, countedOn, notes: notes.trim() || null }, deposit);

  const deposit = rest
    ? {
        fromId: Number(accountId),
        toId: rest.bank.id,
        amount: String(rest.amount),
        sentOn: countedOn,
      }
    : null;

  const setNote = (note: DrawerNote, text: string) =>
    setNotesCount((was) => ({ ...was, notes: { ...was.notes, [note]: text } }));

  const step = (note: DrawerNote, by: number) => {
    const now = Number(notesCount.notes[note].trim() || '0');

    setNote(note, String(Math.max(0, (Number.isFinite(now) ? now : 0) + by)));
  };

  return (
    <div {...enterKeyScope}>
      <PageHeading
        eyebrow="Count & check"
        title={
          account
            ? `Count ${account.kind === 'Till' ? 'the till' : account.name}`
            : 'Count a drawer'
        }
        subtitle="Count what is actually in the drawer. The book is shown beside it, not before it."
        overlap
        actions={
          <Button onClick={onCancel} disabled={busy} shortcut="back">
            Cancel
          </Button>
        }
      >
        {accounts.length > 1 && (
          <div role="group" aria-label="Which drawer" className="flex flex-wrap gap-2">
            {accounts.map((option) => {
              const done = countedToday(option.id, countedOn);

              return (
                <button
                  key={option.id}
                  type="button"
                  disabled={busy}
                  aria-pressed={option.id === accountId}
                  onClick={() => setAccountId(option.id)}
                  className={`h-9 cursor-pointer rounded-full px-3.5 text-[13px] disabled:cursor-not-allowed ${
                    option.id === accountId
                      ? 'bg-[#ffffff] font-semibold text-(--color-hero)'
                      : 'border border-[#ffffff]/35 text-[#ffffff] hover:bg-[#ffffff]/10'
                  }`}
                >
                  {option.name} · {done ? 'counted' : 'not counted'}
                  {countedOn === today ? ' today' : ''}
                </button>
              );
            })}
          </div>
        )}
      </PageHeading>

      <div className="grid grid-cols-12 items-start gap-4 px-4">
        <Card flush className="col-span-12 overflow-hidden xl:col-span-8">
          <div className="border-border-color flex flex-wrap items-center justify-between gap-3 border-b px-5 py-4">
            <div>
              <h2 className="mb-0 text-[17px] font-bold text-gray-900">
                {byNotes ? 'Notes and coins' : 'The total'}
              </h2>
              <p className="text-default mb-0 text-[13px]">
                {byNotes
                  ? 'Type how many of each. The total adds itself up.'
                  : 'Notes and coin in the drawer, as counted.'}
              </p>
            </div>
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                // The figure carries across, so switching never loses a count.
                if (byNotes) {
                  setTypedTotal(countedText(notesCount));
                }

                setByNotes((was) => !was);
              }}
              className="text-primary cursor-pointer text-[13px] font-semibold hover:underline"
            >
              {byNotes ? 'Type the total instead' : 'Count note by note'}
            </button>
          </div>

          {byNotes ? (
            <ul className="mb-0 px-5 ps-5">
              {drawerNotes.map((note, index) => (
                <li
                  key={note}
                  className="border-border-color flex list-none items-center gap-3.5 border-b py-2.5"
                >
                  <span className="bg-light flex h-[38px] w-[72px] shrink-0 items-center justify-center rounded-lg text-[14px] font-bold text-gray-900">
                    ₹{note}
                  </span>
                  <span className="text-default flex-1 text-[13px]" aria-hidden="true">
                    ×
                  </span>
                  <span className="border-border-color flex items-center overflow-hidden rounded-[10px] border">
                    <button
                      type="button"
                      disabled={busy}
                      aria-label={`One fewer ₹${note}`}
                      onClick={() => step(note, -1)}
                      className="bg-light size-10 cursor-pointer text-[18px] text-gray-900"
                    >
                      −
                    </button>
                    <input
                      ref={index === 0 ? firstField : undefined}
                      type="text"
                      inputMode="numeric"
                      aria-label={`How many ₹${note}`}
                      disabled={busy}
                      value={notesCount.notes[note]}
                      onChange={(event) => setNote(note, event.target.value)}
                      className="tabular-nums h-10 w-14 border-0 bg-white text-center text-[15px] font-semibold text-gray-900 outline-none"
                    />
                    <button
                      type="button"
                      disabled={busy}
                      aria-label={`One more ₹${note}`}
                      onClick={() => step(note, 1)}
                      className="bg-light size-10 cursor-pointer text-[18px] text-gray-900"
                    >
                      +
                    </button>
                  </span>
                  <span className="tabular-nums w-28 text-end text-[14px] font-semibold text-gray-900">
                    ₹{formatAmount(noteValue(note, notesCount.notes[note]))}
                  </span>
                </li>
              ))}
              <li className="flex list-none items-center gap-3.5 py-2.5">
                <span className="bg-light flex h-[38px] w-[72px] shrink-0 items-center justify-center rounded-lg text-[13px] font-bold text-gray-900">
                  Coins
                </span>
                <span className="text-default flex-1 text-[13px]">In rupees, all together</span>
                <input
                  type="text"
                  inputMode="numeric"
                  aria-label="Coins, in rupees"
                  disabled={busy}
                  value={notesCount.coins}
                  onChange={(event) =>
                    setNotesCount((was) => ({ ...was, coins: event.target.value }))
                  }
                  className={`${controlClass} tabular-nums w-[136px]! text-end`}
                />
                <span className="w-28" aria-hidden="true" />
              </li>
            </ul>
          ) : (
            <div className="px-5 py-4">
              <Field label="Counted" required>
                <input
                  type="number"
                  min={0}
                  step="0.01"
                  disabled={busy || accounts.length === 0}
                  value={typedTotal}
                  onChange={(event) => setTypedTotal(event.target.value)}
                  onWheel={(event) => event.currentTarget.blur()}
                  className={`${controlClass} max-w-[360px]`}
                />
              </Field>
            </div>
          )}

          <div className="bg-primary-transparent flex items-baseline justify-between px-5 py-3.5">
            <span className="text-primary text-[14px] font-semibold">Counted</span>
            <span className="lining-nums tabular-nums font-serif text-[28px] font-semibold text-gray-900">
              {counted.trim() === '' ? '—' : `₹${formatAmount(Number.isFinite(value) ? value : 0)}`}
            </span>
          </div>
        </Card>

        <aside
          aria-label="Against the book"
          className="col-span-12 flex flex-col gap-4 xl:col-span-4"
        >
          <Card className="flex flex-col gap-2.5">
            <dl className="mb-0 flex flex-col gap-2">
              <Line label="The book says" value={`₹${formatAmount(expected)}`} />
              <Line
                label="Counted"
                value={
                  counted.trim() === ''
                    ? '—'
                    : `₹${formatAmount(Number.isFinite(value) ? value : 0)}`
                }
              />
            </dl>
            <div
              className={`flex items-center justify-between rounded-xl px-3 py-3 ${
                counted.trim() === ''
                  ? 'bg-light text-default'
                  : outcome === 'agrees'
                    ? 'bg-success-transparent text-success'
                    : outcome === 'short'
                      ? 'bg-warning-transparent text-warning'
                      : 'bg-info-transparent text-info'
              }`}
            >
              <span className="text-[14px] font-bold">
                {counted.trim() === ''
                  ? 'Count the drawer first'
                  : outcome === 'agrees'
                    ? 'Agrees with the book'
                    : outcomeLabels[outcome]}
              </span>
              <span className="lining-nums tabular-nums font-serif text-[22px] font-semibold">
                {counted.trim() === '' ? '—' : `₹${formatAmount(Math.abs(variance))}`}
              </span>
            </div>

            {reasonRequired(variance) && (
              <Field
                label="What happened"
                required
                hint="The book is corrected by the difference, with this reason. It cannot be typed in without a count."
              >
                <input
                  type="text"
                  disabled={busy}
                  placeholder="Change given wrong, a slip not entered"
                  value={notes}
                  onChange={(event) => setNotes(event.target.value)}
                  className={controlClass}
                />
              </Field>
            )}

            <Field label="Day counted" required>
              <input
                type="date"
                max={today}
                disabled={busy}
                value={countedOn}
                onChange={(event) => setCountedOn(event.target.value)}
                className={controlClass}
              />
            </Field>

            <div className="flex flex-col gap-2 pt-1.5">
              {deposit && rest && (
                <Button
                  variant="primary"
                  data-enter-key-commit
                  shortcut="save"
                  disabled={problem !== null || busy}
                  onClick={() => record(deposit)}
                  className="justify-center"
                >
                  Record and deposit the rest · ₹{formatAmount(rest.amount)}
                </Button>
              )}
              <Button
                variant={deposit ? 'secondary' : 'primary'}
                icon="icon icon-check"
                {...(deposit ? {} : { 'data-enter-key-commit': true, shortcut: 'save' as const })}
                disabled={problem !== null || busy}
                onClick={() => record(null)}
                className="justify-center"
              >
                {deposit ? 'Record the count only' : 'Record the count'}
              </Button>
            </div>

            {problem && accounts.length > 0 && (
              <p className="text-warning mb-0 text-[length:var(--scale-list-meta)]">{problem}</p>
            )}
          </Card>

          {rest && (
            <Notice tone="info" title={`Keep ₹${formatAmount(rest.keep)} float`}>
              The deposit opens Move money with the rest filled in, {account?.name ?? 'the till'} to{' '}
              {rest.bank.name}. One count per drawer per day.
            </Notice>
          )}

          {variance !== 0 && (
            <Notice tone="warning" title="This will correct the book">
              Recording the count puts ₹{formatAmount(Math.abs(variance))}{' '}
              {variance < 0 ? 'out of' : 'into'} {account?.name ?? 'the account'}, dated the day
              counted, so the balance afterwards is what was counted. The original entries are left
              alone.
            </Notice>
          )}

          {countedOn !== today && (
            <Notice tone="info" title="Counting an earlier day">
              The difference is worked out against that day's closing figure, not today's — so it
              may not match the number above.
            </Notice>
          )}

          {accounts.length === 0 && (
            <Notice tone="warning" title="Nothing to count">
              A drawer appears here once money has moved through it. A bank account is reconciled
              against its statement rather than counted, so it is not offered.
            </Notice>
          )}
        </aside>
      </div>
    </div>
  );
}

function Line({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-2">
      <dt className="text-default text-[13px] font-normal">{label}</dt>
      <dd className="tabular-nums mb-0 text-[13px] font-semibold text-gray-900">{value}</dd>
    </div>
  );
}
